/**
 * SpajzaDialog – quick pantry management from the /intake page.
 * Uses the new pantry v2 endpoints (spajza_polozka) with amount, unit,
 * expiration date and storage location support.
 */

import { useState, useMemo, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ShoppingBasket, Plus, X, Search, Pencil } from "lucide-react";
import type { Potravina } from "../types";

type Jednotka = "g" | "kg" | "ks" | "balenie";

/** Sentinel value used in the Select to represent "no storage location". */
const NO_LOCATION = "__none__";

/** Format a date value (ISO string or YYYY-MM-DD) to YYYY-MM-DD for <input type="date"> */
function toDateInputValue(v: string | null | undefined): string {
  if (!v) return "";
  // If it already looks like YYYY-MM-DD return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  // Otherwise parse and format
  try {
    return new Date(v).toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

interface SpajzaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  klientId: number;
  potraviny: Potravina[];
}

export function SpajzaDialog({
  open,
  onOpenChange,
  klientId,
  potraviny,
}: SpajzaDialogProps) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  // ── Add form state ──────────────────────────────────────────────────────────
  const [foodOpen, setFoodOpen] = useState(false);
  const [foodSearch, setFoodSearch] = useState("");
  const [selectedFood, setSelectedFood] = useState<{
    id: number;
    nazov: string;
    balenieG: string | null;
  } | null>(null);
  const [mnozstvo, setMnozstvo] = useState("1");
  const [jednotka, setJednotka] = useState<Jednotka>("ks");
  const [balenieG, setBalenieG] = useState("");
  const [datumSpotreby, setDatumSpotreby] = useState("");
  const [uloziskoId, setUloziskoId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  // ── Edit state ──────────────────────────────────────────────────────────────
  const [editId, setEditId] = useState<number | null>(null);
  const [editMnozstvo, setEditMnozstvo] = useState("1");
  const [editJednotka, setEditJednotka] = useState<Jednotka>("ks");
  const [editDatumSpotreby, setEditDatumSpotreby] = useState("");

  // Reset the add-form whenever the dialog opens so stale values don't linger
  useEffect(() => {
    if (open) {
      setSelectedFood(null);
      setFoodSearch("");
      setMnozstvo("1");
      setJednotka("ks");
      setBalenieG("");
      setDatumSpotreby("");
      setUloziskoId(null);
      setEditId(null);
    }
  }, [open]);

  const invalidate = () => {
    utils.spajza.getPolozky.invalidate({ klientId });
    utils.spajza.getAvailability.invalidate({ klientId });
  };

  const { data: uloziska = [] } = trpc.spajza.getUloziska.useQuery(
    { klientId },
    { enabled: open && klientId > 0 }
  );

  const { data: polozky = [], isLoading } = trpc.spajza.getPolozky.useQuery(
    { klientId },
    { enabled: open && klientId > 0 }
  );

  const addMutation = trpc.spajza.addPolozka.useMutation();
  const updateMutation = trpc.spajza.updatePolozka.useMutation();
  const deleteMutation = trpc.spajza.deletePolozka.useMutation();
  const updateBalenieGMutation = trpc.potravina.updateBalenieG.useMutation();

  const filteredFoods = useMemo(() => {
    const term = foodSearch.toLowerCase();
    return term
      ? potraviny.filter(p => p.nazov.toLowerCase().includes(term)).slice(0, 60)
      : potraviny.slice(0, 60);
  }, [potraviny, foodSearch]);

  const handleAdd = async () => {
    if (!selectedFood) return;
    setAdding(true);
    try {
      // If balenieG was set and food doesn't have one, update globally
      if (balenieG && !selectedFood.balenieG) {
        await updateBalenieGMutation
          .mutateAsync({ id: selectedFood.id, balenieG })
          .catch(() => {});
      }
      await addMutation.mutateAsync({
        klientId,
        potravinaId: selectedFood.id,
        mnozstvo,
        jednotka,
        balenieG: balenieG || undefined,
        datumSpotreby: datumSpotreby || undefined,
        uloziskoId: uloziskoId ?? undefined,
      });
      toast.success(t("spajza.added", "Food added to pantry"));
      invalidate();
      // Reset add form
      setSelectedFood(null);
      setFoodSearch("");
      setMnozstvo("1");
      setJednotka("ks");
      setBalenieG("");
      setDatumSpotreby("");
      setUloziskoId(null);
    } catch {
      toast.error(t("spajza.addError", "Failed to add food to pantry"));
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMutation.mutateAsync({ id, klientId });
      toast.success(t("spajza.removed", "Item removed from pantry"));
      invalidate();
    } catch {
      toast.error(t("spajza.removeError", "Failed to remove item"));
    }
  };

  const startEdit = (item: (typeof polozky)[0]) => {
    setEditId(item.id);
    setEditMnozstvo(String(item.mnozstvo));
    setEditJednotka((item.jednotka as Jednotka) ?? "ks");
    // Ensure the date is formatted as YYYY-MM-DD for the date input
    setEditDatumSpotreby(toDateInputValue(item.datumSpotreby));
  };

  const handleUpdate = async () => {
    if (!editId) return;
    try {
      await updateMutation.mutateAsync({
        id: editId,
        klientId,
        mnozstvo: editMnozstvo,
        jednotka: editJednotka,
        datumSpotreby: editDatumSpotreby || undefined,
      });
      toast.success(t("spajza.updated", "Item updated"));
      invalidate();
      setEditId(null);
    } catch {
      toast.error(t("spajza.updateError", "Failed to update item"));
    }
  };

  const needsBalenieG =
    selectedFood &&
    !selectedFood.balenieG &&
    (jednotka === "ks" || jednotka === "balenie");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBasket className="h-5 w-5" />
            {t("spajza.title", "My Pantry")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* ── Add food section ──────────────────────────────────────────── */}
          <div className="space-y-3 border rounded-lg p-3">
            <p className="text-sm font-medium">
              {t("spajza.addFood", "Add food to pantry")}
            </p>

            {/* Food picker */}
            <Popover open={foodOpen} onOpenChange={setFoodOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  {selectedFood
                    ? selectedFood.nazov
                    : t("spajza.selectFood", "Select food…")}
                  <Search className="ml-2 h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[380px] p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder={t("spajza.search", "Search food…")}
                    value={foodSearch}
                    onValueChange={setFoodSearch}
                  />
                  <CommandList className="max-h-56 overflow-y-auto">
                    <CommandEmpty>
                      {t("spajza.noFood", "No food found.")}
                    </CommandEmpty>
                    <CommandGroup>
                      {filteredFoods.map(p => (
                        <CommandItem
                          key={p.id}
                          value={p.nazov}
                          onSelect={() => {
                            setSelectedFood({
                              id: p.id!,
                              nazov: p.nazov,
                              balenieG: p.balenieG ?? null,
                            });
                            setBalenieG(p.balenieG ?? "");
                            setFoodOpen(false);
                          }}
                        >
                          {p.nazov}
                          {p.balenieG && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {p.balenieG} g/bal.
                            </span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {selectedFood && (
              <>
                {/* Package size (if needed) */}
                {needsBalenieG && (
                  <div className="space-y-1">
                    <Label className="text-xs">
                      {t("spajza.balenieG", "Package size (g)")}
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={balenieG}
                      onChange={e => setBalenieG(e.target.value)}
                      placeholder="e.g. 500"
                    />
                  </div>
                )}

                {/* Amount + Unit */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">
                      {t("spajza.amount", "Amount")}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={mnozstvo}
                      onChange={e => setMnozstvo(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">
                      {t("spajza.unit", "Unit")}
                    </Label>
                    <Select
                      value={jednotka}
                      onValueChange={v => setJednotka(v as Jednotka)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="g">g</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="ks">ks (pieces)</SelectItem>
                        <SelectItem value="balenie">
                          balenie (package)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Expiry date */}
                <div className="space-y-1">
                  <Label className="text-xs">
                    {t("spajza.expiry", "Expiration date (optional)")}
                  </Label>
                  <Input
                    type="date"
                    value={datumSpotreby}
                    onChange={e => setDatumSpotreby(e.target.value)}
                  />
                </div>

                {/* Storage location — uses NO_LOCATION sentinel instead of "" */}
                {uloziska.length > 0 && (
                  <div className="space-y-1">
                    <Label className="text-xs">
                      {t("spajza.storage", "Storage location")}
                    </Label>
                    <Select
                      value={uloziskoId ? String(uloziskoId) : NO_LOCATION}
                      onValueChange={v =>
                        setUloziskoId(v === NO_LOCATION ? null : Number(v))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t("spajza.noStorage", "No location")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Use sentinel value instead of empty string */}
                        <SelectItem value={NO_LOCATION}>
                          {t("spajza.noStorage", "No location")}
                        </SelectItem>
                        {uloziska.map(u => (
                          <SelectItem key={u.id} value={String(u.id)}>
                            {u.nazov}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button
                  className="w-full"
                  onClick={handleAdd}
                  disabled={adding}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t("spajza.addBtn", "Add to pantry")}
                </Button>
              </>
            )}
          </div>

          {/* ── Current pantry items ──────────────────────────────────────── */}
          <div>
            <p className="text-sm font-medium mb-2">
              {t("spajza.currentItems", "Current pantry")} ({polozky.length})
            </p>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">
                {t("common.loading", "Loading…")}
              </p>
            ) : polozky.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                {t("spajza.empty", "No foods in pantry yet.")}
              </p>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {polozky.map(item => (
                  <div
                    key={item.id}
                    className="flex items-center gap-2 p-2 rounded border text-sm"
                  >
                    {editId === item.id ? (
                      /* ── Inline edit row ─────────────────────────────── */
                      <div className="flex-1 space-y-2">
                        <p className="font-medium text-xs">
                          {item.potravina?.nazov}
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="number"
                            value={editMnozstvo}
                            onChange={e => setEditMnozstvo(e.target.value)}
                            className="h-7 text-xs"
                          />
                          <Select
                            value={editJednotka}
                            onValueChange={v => setEditJednotka(v as Jednotka)}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="g">g</SelectItem>
                              <SelectItem value="kg">kg</SelectItem>
                              <SelectItem value="ks">ks</SelectItem>
                              <SelectItem value="balenie">balenie</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Input
                          type="date"
                          value={editDatumSpotreby}
                          onChange={e => setEditDatumSpotreby(e.target.value)}
                          className="h-7 text-xs"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={handleUpdate}
                            className="h-7 text-xs flex-1"
                          >
                            {t("common:save", "Save")}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditId(null)}
                            className="h-7 text-xs"
                          >
                            {t("common:cancel", "Cancel")}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* ── Display row ─────────────────────────────────── */
                      <>
                        <div className="flex-1 min-w-0">
                          <span className="font-medium truncate block">
                            {item.potravina?.nazov}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {item.mnozstvo} {item.jednotka}
                            {item.datumSpotreby &&
                              ` · exp: ${toDateInputValue(item.datumSpotreby)}`}
                            {item.ulozisko && ` · ${item.ulozisko.nazov}`}
                          </span>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6"
                          onClick={() => startEdit(item)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 text-destructive"
                          onClick={() => handleDelete(item.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
